import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Typography,
  Menu,
  MenuItem,
  Divider,
  Tooltip,
  useTheme,
  Drawer
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import LogoutIcon from '@mui/icons-material/Logout';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DashboardIcon from '@mui/icons-material/Dashboard';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import PaymentIcon from '@mui/icons-material/Payment';
import AddTaskIcon from '@mui/icons-material/AddTask';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import { logout } from '../../store/actions/authActions';

const DRAWER_WIDTH = 240;

const WorkspaceSidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const theme = useTheme();
  const { user, isAuthenticated } = useSelector((state) => state.auth);
  const [anchorEl, setAnchorEl] = useState(null);

  // 侧边栏菜单项
  const menuItems = [
    { label: '工作台', icon: <DashboardIcon />, path: '/workspace' },
    { label: '创建任务', icon: <AddTaskIcon />, path: '/workspace/create' },
    { label: '充值中心', icon: <PaymentIcon />, path: '/workspace/recharge' },
    { label: '点数记录', icon: <AccountBalanceWalletIcon />, path: '/workspace/points' },
    { label: '专属客服', icon: <SupportAgentIcon />, path: '/workspace/customer-service' },
  ];

  // 处理用户菜单打开
  const handleOpenMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  // 处理用户菜单关闭
  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  // 处理导航
  const handleNavigate = (path) => {
    navigate(path);
  };

  // 处理个人中心
  const handleProfile = () => {
    handleCloseMenu();
    navigate('/profile');
  };

  // 处理退出登录
  const handleLogout = () => {
    handleCloseMenu();
    dispatch(logout());
    navigate('/landing');
  };

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: DRAWER_WIDTH,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: DRAWER_WIDTH,
          boxSizing: 'border-box',
          borderRight: `1px solid ${theme.palette.divider}`,
          backgroundColor: 'white',
        },
      }}
    >
      {/* 侧边栏顶部 - 返回首页 */}
      <Box sx={{ p: 2 }}>
        <ListItemButton
          onClick={() => handleNavigate('/landing')}
          sx={{
            borderRadius: 2,
            '&:hover': {
              backgroundColor: theme.palette.action.hover,
            },
          }}
        >
          <ListItemIcon sx={{ minWidth: 40 }}>
            <ArrowBackIcon sx={{ color: theme.palette.primary.main }} />
          </ListItemIcon>
          <ListItemText
            primary="返回首页"
            primaryTypographyProps={{
              fontSize: '0.875rem',
              fontWeight: 500,
              color: theme.palette.primary.main,
            }}
          />
        </ListItemButton>
      </Box>

      <Divider />

      {/* 菜单列表 */}
      <List sx={{ px: 1, py: 2 }}>
        {menuItems.map((item) => (
          <ListItem key={item.path} disablePadding sx={{ mb: 0.5 }}>
            <ListItemButton
              onClick={() => handleNavigate(item.path)}
              selected={location.pathname === item.path}
              sx={{
                borderRadius: 2,
                '&.Mui-selected': {
                  backgroundColor: theme.palette.primary.main + '10',
                  '&:hover': {
                    backgroundColor: theme.palette.primary.main + '15',
                  },
                },
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 40, color: theme.palette.primary.main }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText
                primary={item.label}
                primaryTypographyProps={{
                  fontSize: '0.875rem',
                  fontWeight: location.pathname === item.path ? 600 : 400,
                  color: location.pathname === item.path ? theme.palette.primary.main : 'text.primary',
                }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>

      {/* 底部用户信息 */}
      <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, p: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
        {isAuthenticated && user ? (
          <Box>
            <Tooltip title="账户设置">
              <Box
                onClick={handleOpenMenu}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  p: 1,
                  borderRadius: 2,
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: theme.palette.action.hover,
                  },
                }}
              >
                <Avatar
                  alt={user.username}
                  src={user.avatar || ''}
                  sx={{
                    width: 36,
                    height: 36,
                    bgcolor: theme.palette.primary.main,
                    color: 'white',
                    fontSize: '0.875rem',
                  }}
                >
                  {user.username.charAt(0).toUpperCase()}
                </Avatar>
                <Box sx={{ ml: 1.5, flex: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary', lineHeight: 1.2 }}>
                    {user.username}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {user.points} 点数
                  </Typography>
                </Box>
              </Box>
            </Tooltip>

            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleCloseMenu}
              PaperProps={{
                elevation: 2,
                sx: {
                  overflow: 'visible',
                  filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.08))',
                  mt: 1,
                  borderRadius: 2,
                  minWidth: 160,
                  '& .MuiMenuItem-root': {
                    px: 2,
                    py: 1,
                    borderRadius: 1,
                    mx: 0.5,
                    my: 0.25,
                    '&:hover': {
                      backgroundColor: theme.palette.background.light,
                    },
                  },
                },
              }}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
              <MenuItem onClick={handleProfile}>
                <PersonIcon sx={{ mr: 1, fontSize: 18 }} />
                个人中心
              </MenuItem>
              <MenuItem onClick={handleLogout}>
                <LogoutIcon sx={{ mr: 1, fontSize: 18 }} />
                退出登录
              </MenuItem>
            </Menu>
          </Box>
        ) : (
          <Box />
        )}
      </Box>
    </Drawer>
  );
};

export default WorkspaceSidebar;
export { DRAWER_WIDTH };